<script setup>
import {computed, ref} from "vue";
import trackersService from "@/services/trackers.service";
import ColumnsSelect from "@/components/manage/ColumnsSelect.vue";
import userColumnsService from "@/services/user-columns.service";

const {tableType, trackerId, trackerType} = defineProps({
    tableType: String,
    trackerId: {
        type: Number,
        default: 0,
    },
    trackerType: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['changed']);

const showModal = ref(false);
const defaultSettings = ref(false);
const canEditDefaultColumns = ref(false);
const columns = ref([]);
const showConfirm = ref(false);
const hasPersonalSettings = ref(true);
const hasDefaultSettings = ref(false);
const canLoadColumns = ref(false);

const useType = computed(() => defaultSettings.value ? 'default' : 'personal');

async function getData() {
    if (trackerId) {
        const {data: permissions} = await trackersService.getUserPermissions(trackerId);

        if (permissions.includes('tracker-manage-default-columns')) {
            const {data: hasSettings} = await userColumnsService.hasSettings(tableType, trackerId);

            canEditDefaultColumns.value = true;
            hasPersonalSettings.value = hasSettings.personal;
            hasDefaultSettings.value = hasSettings.default;

            if (hasDefaultSettings.value && !hasPersonalSettings.value) {
                defaultSettings.value = true;
            }
        }
    }

    canLoadColumns.value = true;
}

function handleSave() {
    if (defaultSettings.value && hasPersonalSettings.value) {
        showConfirm.value = true;
    } else {
        save();
    }
}

async function handleConfirm(removePersonalColumns) {
    showConfirm.value = false;

    if (removePersonalColumns) {
        await userColumnsService.removePersonalColumns(trackerId);
    }

    save();
}

async function save() {
    showModal.value = false;
    await userColumnsService.updateColumns(tableType, useType.value, trackerId, columns.value);

    emit("changed");
}

function setColumns(newColumns) {
    columns.value = newColumns;
}

function open() {
    defaultSettings.value = false;
    showModal.value = true;

    getData();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal title="Manage Columns" v-model="showModal" id="manage-columns" modal-class="modal-w-middle">
        <div v-if="canEditDefaultColumns" class="content-switcher">
            <span>Personal settings</span>
            <div class="content-switcher__slider">
                <input
                    v-model="defaultSettings"
                    type="checkbox"
                    id="content-switcher__slider"
                >
                <label for="content-switcher__slider"></label>
            </div>

            <span>Tracker-wide settings</span>
        </div>

        <p>Select the columns you want to display on the table and rearrange their order.</p>

        <header class="manage-columns__table-header">
            <p>Field</p>
            <p>Category</p>
        </header>

        <ColumnsSelect
            v-if="canLoadColumns"
            :table-type="tableType"
            :use-type="useType"
            :tracker-type="trackerType"
            :tracker-id="trackerId"
            class="list-manage mb-4"
            @changed="setColumns"
        />

        <template #modal-footer>
            <b-button variant="primary" size="lg" @click="handleSave">Save</b-button>
        </template>

        <b-modal v-model="showConfirm" title="Apply tracker-wide settings to my view?">
            <template #modal-footer>
                <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                    <b-button variant="primary" @click="handleConfirm(true)">Yes, replace personal settings</b-button>
                    <b-button variant="primary" @click="handleConfirm(false)">No, keep personal settings</b-button>
                </div>
            </template>
        </b-modal>
    </b-modal>
</template>

<style scoped>

</style>
